import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Divider,
  Typography,
} from "@mui/material";

import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Link as LinkIcon,
} from "@mui/icons-material";

import useAdminContext from "../../store/contexts/AdminContext";
import {
  AD_FORMATS,
  AD_STATUS,
  AD_CONFIG,
  USER_ACCOUNT_TYPES,
} from "../../constants/types";

const CreateAdModal = ({ open, onClose, onSuccess }) => {
  const { createAdApiCall, authState } = useAdminContext();
  const { loading } = authState;

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    ad_type: AD_FORMATS.IMAGE.value,
    imageUrl: "",
    targetUrl: "",
    impressionGoal: "",
    priority: AD_CONFIG.DEFAULT_PRIORITY,
    startDate: new Date().toISOString().substring(0, 10),
    endDate: "",
    visiblePlans: Object.values(USER_ACCOUNT_TYPES).map((p) => p.value),
    status: AD_STATUS.DRAFT.value,
  });

  const [errors, setErrors] = useState({});

  // Format number with commas
  const formatNumber = (num) => {
    if (!num) return "";
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const removeCommas = (value) => value.replace(/,/g, "");

  const handleChange = (field) => (e) => {
    let value = e.target.value;

    // Auto-format impression goal with commas
    if (field === "impressionGoal") {
      const cleaned = removeCommas(value);

      if (!/^\d*$/.test(cleaned)) return; // only allow numbers

      value = formatNumber(cleaned);
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.description.trim())
      newErrors.description = "Description is required";

    if (!formData.imageUrl.trim()) newErrors.imageUrl = "Image URL is required";
    if (!formData.targetUrl.trim())
      newErrors.targetUrl = "Target URL is required";

    if (formData.endDate && formData.endDate < formData.startDate)
      newErrors.endDate = "End date must be after start date";

    if (formData.impressionGoal) {
      const goal = parseInt(removeCommas(formData.impressionGoal), 10);
      if (isNaN(goal) || goal < 1)
        newErrors.impressionGoal = "Impression goal must be a positive number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      title: formData.title,
      description: formData.description,
      ad_type: formData.ad_type,
      priority: parseInt(formData.priority, 10),

      start_date: new Date(formData.startDate),
      end_date: formData.endDate ? new Date(formData.endDate) : null,

      visible_to_plans: formData.visiblePlans,
      click_url: formData.targetUrl,
      status: formData.status,

      impressions_goal: formData.impressionGoal
        ? parseInt(removeCommas(formData.impressionGoal), 10)
        : null,

      metadata: {
        image_url: formData.imageUrl,
        status: formData.status,
      },
    };

    const res = await createAdApiCall(payload);
    if (res) {
      handleClose();
      onSuccess?.();
    }
  };

  const handleClose = () => {
    setFormData({
      title: "",
      description: "",
      ad_type: AD_FORMATS.IMAGE.value,
      imageUrl: "",
      targetUrl: "",
      impressionGoal: "",
      priority: AD_CONFIG.DEFAULT_PRIORITY,
      startDate: new Date().toISOString().substring(0, 10),
      endDate: "",
      visiblePlans: Object.values(USER_ACCOUNT_TYPES).map((p) => p.value),
      status: AD_STATUS.DRAFT.value,
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h5">Create New Advertisement</Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          {/* Title */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={handleChange("title")}
              error={!!errors.title}
              helperText={errors.title}
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description"
              value={formData.description}
              onChange={handleChange("description")}
              error={!!errors.description}
              helperText={errors.description}
            />
          </Grid>

          {/* Image URL */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Image URL"
              value={formData.imageUrl}
              onChange={handleChange("imageUrl")}
              error={!!errors.imageUrl}
              helperText={errors.imageUrl}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <UploadIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Target URL */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Target URL"
              value={formData.targetUrl}
              onChange={handleChange("targetUrl")}
              error={!!errors.targetUrl}
              helperText={errors.targetUrl}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Impression Goal (with commas) */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Impression Goal"
              value={formData.impressionGoal}
              onChange={handleChange("impressionGoal")}
              error={!!errors.impressionGoal}
              helperText={errors.impressionGoal}
              InputProps={{
                inputMode: "numeric",
              }}
            />
          </Grid>

          {/* Ad Type */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Ad Type</InputLabel>
              <Select
                value={formData.ad_type}
                onChange={handleChange("ad_type")}
                label="Ad Type"
              >
                {Object.values(AD_FORMATS).map((format) => (
                  <MenuItem key={format.value} value={format.value}>
                    {format.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Priority */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Priority (1–100)"
              value={formData.priority}
              onChange={handleChange("priority")}
              inputProps={{ min: 1, max: 100 }}
            />
          </Grid>

          {/* Start Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={formData.startDate}
              onChange={handleChange("startDate")}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* End Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={formData.endDate}
              onChange={handleChange("endDate")}
              error={!!errors.endDate}
              helperText={errors.endDate}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Visible Plans */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Visible To Plans</InputLabel>
              <Select
                multiple
                value={formData.visiblePlans}
                onChange={handleChange("visiblePlans")}
                label="Visible To Plans"
              >
                {Object.values(USER_ACCOUNT_TYPES).map((plan) => (
                  <MenuItem key={plan.value} value={plan.value}>
                    {plan.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Status */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={handleChange("status")}
                label="Status"
              >
                {Object.values(AD_STATUS).map((st) => (
                  <MenuItem key={st.value} value={st.value}>
                    {st.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3 }}>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" disabled={loading} onClick={handleSubmit}>
          {loading ? "Creating..." : "Create Ad"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAdModal;
